import React from 'react';
import { Link } from 'react-router-dom';
import { XCircle, ShoppingCart, ArrowRight, Mail, Shield, Truck, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const PaymentCancelledPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8 md:py-12 px-4">
      <SEO 
        title="Paiement non finalisé | Atlantique Spa Boutique"
        description="Votre paiement n'a pas été finalisé. Aucun montant n'a été débité. Vous pouvez reprendre votre commande à tout moment."
      />
      
      <div className="max-w-2xl mx-auto space-y-6">
        
        {/* ========== BLOC 1: Paiement non finalisé ========== */}
        <Card className="overflow-hidden border-0 shadow-lg">
          <div className="bg-gradient-to-r from-slate-600 to-slate-700 p-8 text-center">
            <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Paiement non finalisé
            </h1>
            <p className="text-slate-200 text-lg">
              Votre paiement n'a pas été finalisé.
            </p>
          </div>
          
          <CardContent className="p-6 md:p-8">
            {/* Message rassurant */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-6">
              <p className="text-emerald-800 font-medium flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-emerald-600" />
                Aucun montant n'a été débité
              </p>
              <p className="text-emerald-700 text-sm">
                Votre compte bancaire n'a pas été affecté par cette tentative.
              </p>
            </div>
            
            {/* Raisons possibles */}
            <div className="mb-6">
              <p className="text-slate-700 mb-4">
                Cela peut arriver pour plusieurs raisons :
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-1">–</span>
                  <span>Interruption pendant le paiement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-1">–</span>
                  <span>Refus temporaire de la banque</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-1">–</span>
                  <span>Problème de connexion</span>
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* ========== BLOC 2: Actions - Reprendre sereinement ========== */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6 md:p-8 text-center">
            <p className="text-slate-600 mb-6">
              Vous pouvez reprendre votre commande à tout moment.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/panier" className="flex-1">
                <Button className="w-full bg-[#F97316] hover:bg-[#EA580C] py-6 text-base">
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Retourner au panier
                </Button>
              </Link>
              <Link to="/pieces-accessoires" className="flex-1">
                <Button variant="outline" className="w-full py-6 text-base border-slate-300">
                  Continuer mes achats
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* ========== BLOC 3: Service client (EMAIL UNIQUEMENT) ========== */}
        <Card className="border-0 shadow-lg bg-slate-800 text-white">
          <CardContent className="p-6 md:p-8 text-center">
            <Mail className="w-10 h-10 text-slate-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">
              Besoin d'aide ou d'une information ?
            </h2>
            <p className="text-slate-300 mb-4">
              Contactez-nous par e-mail à :
            </p>
            <a 
              href="mailto:contact@atlantiquespaboutique.com" 
              className="inline-block text-white font-semibold text-lg bg-white/10 hover:bg-white/20 px-6 py-3 rounded-xl transition-colors"
            >
              contact@atlantiquespaboutique.com
            </a>
            <p className="text-slate-400 text-sm mt-4">
              Nous vous répondrons dans les meilleurs délais.
            </p>
          </CardContent>
        </Card>

        {/* ========== BLOC 4: Rassurance ========== */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-5 shadow-md text-center border border-slate-100">
            <Shield className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
            <p className="font-medium text-slate-900 text-sm">Paiement sécurisé</p>
            <p className="text-xs text-slate-500 mt-1">via Stripe</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-md text-center border border-slate-100">
            <Truck className="w-8 h-8 text-blue-600 mx-auto mb-3" />
            <p className="font-medium text-slate-900 text-sm">Livraison France entière</p>
            <p className="text-xs text-slate-500 mt-1">Offerte dès 80 € TTC</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-md text-center border border-slate-100">
            <CreditCard className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <p className="font-medium text-slate-900 text-sm">Aucun débit</p>
            <p className="text-xs text-slate-500 mt-1">sans validation complète</p>
          </div>
        </div>

        {/* ========== Lien retour accueil ========== */}
        <div className="text-center pt-4">
          <Link to="/" className="text-slate-500 hover:text-slate-700 text-sm underline">
            Retour à l'accueil
          </Link>
        </div>
        
      </div>
    </div>
  );
};

export default PaymentCancelledPage;
